"use client";

import { ChevronDown, Mail, Phone, Clock, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RangeInput } from "@/components/ui/range-input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ProjectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border bg-card">
        <p className="font-default text-2xl text-foreground mb-4">{successMessage}</p>
        <p className="text-sm text-muted-foreground mb-6">
          Our studio will reach out to you shortly to discuss your acquisition.
        </p>
        <Button onClick={resetForm} variant="outline" className="">
          Submit another inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Your name is required",
      minLength: {
        value: 2,
        message: "Please enter at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-2 border-b border-border pb-2 focus-within:border-foreground transition-colors duration-500">
            <FormLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono">
              Full Name
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="John Doe" className="border-0 bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-2 border-b border-border pb-2 focus-within:border-foreground transition-colors duration-500">
            <FormLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono">
              Email Address
            </FormLabel>
            <FormControl>
              <Input {...field} type="email" placeholder="john@example.com" className="border-0 bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem className="space-y-2 border-b border-border pb-2 focus-within:border-foreground transition-colors duration-500">
            <FormLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input {...field} type="tel" placeholder="+1 (555) 123-4567" className="border-0 bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_type" render={({
      field
    }) => <FormItem className="space-y-2 border-b border-border pb-2 focus-within:border-foreground transition-colors duration-500">
            <FormLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono">
              Inquiry Type
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="border-0 bg-transparent px-0 h-10 text-base text-foreground focus-visible:ring-0 [&>svg]:text-foreground">
                  <SelectValue placeholder="Select an inquiry type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover border border-border">
                <SelectItem value="print_purchase">Fine Art Print Purchase</SelectItem>
                <SelectItem value="licensing">Commercial Licensing</SelectItem>
                <SelectItem value="commission">Private Commission</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_location" render={({
      field
    }) => <FormItem className="space-y-2 border-b border-border pb-2 focus-within:border-foreground transition-colors duration-500">
            <FormLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono">
              Location
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="City, Country" className="border-0 bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget" rules={{
      min: {
        value: 0,
        message: "Budget must be positive"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-3 border-b border-border pb-4 focus-within:border-foreground transition-colors duration-500">
            <div className="flex items-baseline justify-between">
              <FormLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono">
                Budget Range
              </FormLabel>
              <span className="font-mono text-sm text-foreground">
                ${Number(field.value || 5000).toLocaleString()}
              </span>
            </div>
            <FormControl>
              <RangeInput min={1000} max={100000} step={500} value={Number(field.value || 5000)} onValueChange={v => field.onChange(v)} className="w-full h-1 bg-border appearance-none cursor-pointer accent-foreground" />
            </FormControl>
            <div className="flex justify-between text-[10px] font-mono text-muted-foreground/60">
              <span>$1K</span>
              <span>$25K</span>
              <span>$50K</span>
              <span>$75K</span>
              <span>$100K+</span>
            </div>
          </FormItem>} />

      <FormField control={control} name="project_description" rules={{
      required: "Please tell us about your interest",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-2 border-b border-border pb-2 focus-within:border-foreground transition-colors duration-500">
            <FormLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono">
              Details
            </FormLabel>
            <FormControl>
              <Textarea {...field} rows={4} placeholder="Describe your interest in the work..." className="border-0 bg-transparent px-0 min-h-28 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-foreground text-background hover:bg-foreground/90 font-mono text-xs uppercase tracking-[0.2em]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : "Submit Inquiry"}
        </Button>
        <p className="text-center text-xs text-muted-foreground mt-4 font-mono uppercase tracking-[0.15em]">
          We respond within 24 hours
        </p>
      </div>
    </Form>;
}
export default function Inquiry() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const clientTypes = ["COLLECTOR", "GALLERY", "ARCHITECT"];
  return <section data-section-id="2455" id="acquisition-inquiry" className="bg-background text-foreground pt-32 md:pt-44 lg:pt-[200px] pb-20 md:pb-28 lg:pb-32 px-6 md:px-10 lg:px-16 relative overflow-hidden">
      <div className="absolute top-1/4 -left-40 w-[500px] h-[500px] pointer-events-none opacity-30" style={{
      background: "radial-gradient(circle, hsla(0, 0%, 50%, 0.15) 0%, transparent 70%)",
      filter: "blur(60px)"
    }} />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
          <div ref={leftRef} className={`flex flex-col transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-12 bg-foreground" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-foreground">
                Acquisition
              </span>
            </div>

            <h2 className="font-default font-light text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight text-foreground mb-8">
              Acquire a<br />
              piece.
            </h2>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-md mb-12">
              Limited production, ultra high end film fine photography. Each acquisition is handled with the discretion and care of a private gallery.
            </p>

            <div className="space-y-5 mb-16">
              <div className="flex items-center gap-4">
                <Mail className="size-4 text-foreground stroke-[1.5]" />
                <Link to="mailto:contact@johnjohnphotography.com" className="text-foreground text-sm hover:text-muted-foreground transition-colors">
                  contact@johnjohnphotography.com
                </Link>
              </div>
              <div className="flex items-center gap-4">
                <Phone className="size-4 text-foreground stroke-[1.5]" />
                <Link to="tel:+15559876543" className="text-foreground text-sm hover:text-muted-foreground transition-colors">
                  +1 (555) 987-6543
                </Link>
              </div>
              <div className="flex items-center gap-4">
                <Clock className="size-4 text-foreground stroke-[1.5]" />
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                  Mon–Fri 9–6 · Sat 10–4
                </span>
              </div>
            </div>

            <div className="mt-auto">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 mb-4">
                We work with
              </p>
              <div className="flex flex-wrap gap-3">
                {clientTypes.map((type, i) => <span key={type} data-index={i} className="px-4 py-2 border border-border font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:border-foreground hover:text-foreground transition-colors duration-300">
                    {type}
                  </span>)}
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative">
              <div className="absolute -top-2 -left-2 w-6 h-6 border-t border-l border-border pointer-events-none" />
              <div className="absolute -top-2 -right-2 w-6 h-6 border-t border-r border-border pointer-events-none" />
              <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b border-l border-border pointer-events-none" />
              <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b border-r border-border pointer-events-none" />

              <div className="bg-card border border-border p-8 md:p-12">
                <div className="flex items-baseline justify-between mb-10 pb-6 border-b border-border">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                    Acquisition Inquiry
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground">
                    FORM / 001
                  </span>
                </div>

                <FormProvider formKey="acquisition_inquiry_form_gallery_home" formId="acquisition_inquiry_form_gallery_home" sectionName="acquisition-inquiry" defaultValues={{
                name: "",
                email: "",
                phone: "",
                project_type: "",
                project_location: "",
                budget: 5000,
                project_description: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true
                },
                project_description: {
                  required: true,
                  minLength: 10
                },
                budget: {
                  min: 0
                }
              }} submitText="Submit Inquiry" successMessage="Thank you. Your inquiry has been received." errorMessage="Something went wrong. Please try again or email us directly.">
                  <ProjectForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}